import type { ProductVariant } from '@/api/models';
import {
    isProductBarcodeErrorCode,
    type ProductBarcodeErrorCode,
    type VariantBarcodesUpdateRequest,
} from '@/api/handlers/resources/products';

/** The longest code bos-api keeps, after trimming (`BARCODE_TOO_LONG`). */
export const BARCODE_MAX_LENGTH = 50;

/** One alternate in the drawer; `key` stays with its field across edits. */
export type AlternateBarcodeDraft = { key: string; value: string };

/** What the barcodes drawer holds for one variant while it is edited. */
export type VariantBarcodesDraft = {
    main: string;
    alternates: AlternateBarcodeDraft[];
    /** The "Add a barcode" field: added to the alternates by Add (or Save). */
    addText: string;
    /** The next alternate's key, so a removed field's key is never reused. */
    nextKey: number;
};

/** The main barcode field, the add field, or an alternate's field by its key. */
export type BarcodeFieldId = 'main' | 'add' | string;

/** A field's error as a locale key under `products.detail.barcodes.errors`. */
export type BarcodeFieldError = {
    key: string;
    values?: Record<string, number>;
};

export type BarcodeFieldErrors = Partial<
    Record<BarcodeFieldId, BarcodeFieldError>
>;

const ERRORS = 'products.detail.barcodes.errors';

const errorOf = (
    code: ProductBarcodeErrorCode,
    field: BarcodeFieldId,
): BarcodeFieldError => {
    if (code === 'BARCODE_TOO_LONG') {
        return {
            key: `${ERRORS}.${code}`,
            values: { max: BARCODE_MAX_LENGTH },
        };
    }
    if (code === 'BARCODE_REQUIRED' && field !== 'main') {
        return { key: `${ERRORS}.BARCODE_REQUIRED_alternate` };
    }
    return { key: `${ERRORS}.${code}` };
};

/** Compared as bos-api compares within a variant: trimmed, case-insensitive. */
const sameCode = (left: string, right: string): boolean =>
    left.trim().toLocaleLowerCase('en') ===
    right.trim().toLocaleLowerCase('en');

/** The drawer's starting point: the variant's codes as saved. */
export const seedBarcodesDraft = (
    variant: ProductVariant,
): VariantBarcodesDraft => ({
    main: variant.barcode,
    alternates: variant.alternateBarcodes.map((value, index) => ({
        key: `alt-${index}`,
        value,
    })),
    addText: '',
    nextKey: variant.alternateBarcodes.length,
});

/** The whole set the PUT sends, every code trimmed. */
export const barcodesRequestOf = (
    draft: VariantBarcodesDraft,
): VariantBarcodesUpdateRequest => ({
    barcode: draft.main.trim(),
    alternateBarcodes: draft.alternates.map((alternate) =>
        alternate.value.trim(),
    ),
});

/** Whether Save would change anything, a code typed in the add field included. */
export const isBarcodesDraftDirty = (
    draft: VariantBarcodesDraft,
    variant: ProductVariant,
): boolean => {
    if (draft.addText.trim().length > 0) {
        return true;
    }
    const request = barcodesRequestOf(draft);
    return (
        request.barcode !== variant.barcode ||
        request.alternateBarcodes.length !== variant.alternateBarcodes.length ||
        request.alternateBarcodes.some(
            (code, index) => code !== variant.alternateBarcodes[index],
        )
    );
};

/**
 * Adds the add field's code as the last alternate and empties the field.
 * A blank field changes nothing; a code over 50 characters, or one the
 * variant already has (main or alternate), stays in the field with its
 * error instead.
 */
export const addAlternateBarcode = (
    draft: VariantBarcodesDraft,
): { draft: VariantBarcodesDraft; error: BarcodeFieldError | null } => {
    const code = draft.addText.trim();
    if (code.length === 0) {
        return { draft, error: null };
    }
    if (code.length > BARCODE_MAX_LENGTH) {
        return { draft, error: errorOf('BARCODE_TOO_LONG', 'add') };
    }
    const taken =
        sameCode(code, draft.main) ||
        draft.alternates.some((alternate) => sameCode(code, alternate.value));
    if (taken) {
        return { draft, error: errorOf('BARCODE_DUPLICATE', 'add') };
    }
    return {
        draft: {
            ...draft,
            alternates: [
                ...draft.alternates,
                { key: `alt-${draft.nextKey}`, value: code },
            ],
            addText: '',
            nextKey: draft.nextKey + 1,
        },
        error: null,
    };
};

export const removeAlternateBarcode = (
    draft: VariantBarcodesDraft,
    key: string,
): VariantBarcodesDraft => ({
    ...draft,
    alternates: draft.alternates.filter((alternate) => alternate.key !== key),
});

/**
 * Swaps an alternate with the main barcode: the alternate becomes main and
 * the old main takes its place in the list (a blank old main is dropped).
 */
export const makeAlternateMain = (
    draft: VariantBarcodesDraft,
    key: string,
): VariantBarcodesDraft => {
    const picked = draft.alternates.find((alternate) => alternate.key === key);
    if (!picked) {
        return draft;
    }
    const oldMain = draft.main.trim();
    return {
        ...draft,
        main: picked.value,
        alternates: draft.alternates.flatMap((alternate) => {
            if (alternate.key !== key) {
                return [alternate];
            }
            return oldMain.length === 0 ? [] : [{ key, value: oldMain }];
        }),
    };
};

/**
 * bos-api's shape rules, checked before the request so a mistake is shown on
 * its field at once: a main barcode, no blank alternate, nothing over 50
 * characters, and no code twice (an alternate equal to the main, or to an
 * earlier alternate, is the one marked). `BARCODE_TAKEN` needs the server.
 */
export const checkBarcodesDraft = (
    draft: VariantBarcodesDraft,
): BarcodeFieldErrors => {
    const errors: BarcodeFieldErrors = {};
    const main = draft.main.trim();
    if (main.length === 0) {
        errors.main = errorOf('BARCODE_REQUIRED', 'main');
    } else if (main.length > BARCODE_MAX_LENGTH) {
        errors.main = errorOf('BARCODE_TOO_LONG', 'main');
    }
    draft.alternates.forEach((alternate, index) => {
        const code = alternate.value.trim();
        if (code.length === 0) {
            errors[alternate.key] = errorOf('BARCODE_REQUIRED', alternate.key);
        } else if (code.length > BARCODE_MAX_LENGTH) {
            errors[alternate.key] = errorOf('BARCODE_TOO_LONG', alternate.key);
        } else if (
            (main.length > 0 && sameCode(code, main)) ||
            draft.alternates
                .slice(0, index)
                .some((earlier) => sameCode(code, earlier.value))
        ) {
            errors[alternate.key] = errorOf('BARCODE_DUPLICATE', alternate.key);
        }
    });
    return errors;
};

/** The fields in the order the drawer shows them, for the first-invalid focus. */
export const barcodeFieldOrder = (
    draft: VariantBarcodesDraft,
): BarcodeFieldId[] => [
    'main',
    ...draft.alternates.map((alternate) => alternate.key),
    'add',
];

/** The first field, in the drawer's order, that has an error. */
export const firstInvalidBarcodeField = (
    draft: VariantBarcodesDraft,
    errors: BarcodeFieldErrors,
): BarcodeFieldId | null =>
    barcodeFieldOrder(draft).find((field) => errors[field] !== undefined) ??
    null;

/** The code a refusal's message names: `Barcode '123' is …`. */
const namedCodeOf = (message: string | undefined): string | null => {
    const match = /'(.*)'/.exec(message ?? '');
    return match?.[1] ?? null;
};

/**
 * Where a refusal of the barcodes PUT belongs, or `null` for any other
 * failure (the toast's). `BARCODE_TOO_LONG`, `BARCODE_DUPLICATE` and
 * `BARCODE_TAKEN` go on the field holding the code the message names: for a
 * duplicate the later of the two (an alternate equal to the main is the
 * alternate). `BARCODE_REQUIRED` goes on a blank main barcode, else on the
 * first blank alternate. Without a field to match, the draft's own check
 * places it, and failing that the main barcode carries it.
 */
export const placeBarcodesRefusal = (
    draft: VariantBarcodesDraft,
    refusal: { code?: string; message?: string },
): BarcodeFieldErrors | null => {
    const { code } = refusal;
    if (!isProductBarcodeErrorCode(code)) {
        return null;
    }
    const at = (field: BarcodeFieldId): BarcodeFieldErrors => ({
        [field]: errorOf(code, field),
    });

    if (code === 'BARCODE_REQUIRED') {
        if (draft.main.trim().length === 0) {
            return at('main');
        }
        const blank = draft.alternates.find(
            (alternate) => alternate.value.trim().length === 0,
        );
        return at(blank ? blank.key : 'main');
    }

    const named = namedCodeOf(refusal.message);
    if (named !== null) {
        const holders: BarcodeFieldId[] = [
            ...(sameCode(draft.main, named) ? ['main'] : []),
            ...draft.alternates
                .filter((alternate) => sameCode(alternate.value, named))
                .map((alternate) => alternate.key),
        ];
        const field =
            code === 'BARCODE_DUPLICATE' ? holders.at(-1) : holders[0];
        if (field !== undefined) {
            return at(field);
        }
    }

    const checked = checkBarcodesDraft(draft);
    const fallback = barcodeFieldOrder(draft).find(
        (field) => checked[field]?.key === errorOf(code, field).key,
    );
    return at(fallback ?? 'main');
};
